import { PublicClientApplication, Configuration, AuthenticationResult, AccountInfo } from '@azure/msal-browser';

// Microsoft OAuth Configuration
const msalConfig: Configuration = {
  auth: {
    clientId: process.env.REACT_APP_MICROSOFT_CLIENT_ID || 'your-microsoft-client-id',
    authority: 'https://login.microsoftonline.com/common',
    redirectUri: process.env.REACT_APP_REDIRECT_URI || window.location.origin,
  },
  cache: {
    cacheLocation: 'sessionStorage',
    storeAuthStateInCookie: false,
  },
};

// Google OAuth Configuration
const GOOGLE_CLIENT_ID = process.env.REACT_APP_GOOGLE_CLIENT_ID || 'your-google-client-id';

// Initialize MSAL instance
const msalInstance = new PublicClientApplication(msalConfig);

export interface OAuthUser {
  id: string;
  email: string;
  name: string;
  picture?: string;
  provider: 'microsoft' | 'google';
  organization?: string;
}

export interface OAuthTokens {
  accessToken: string;
  refreshToken?: string;
  idToken?: string;
  expiresAt: number;
}

class OAuthService {
  private static instance: OAuthService;

  public static getInstance(): OAuthService {
    if (!OAuthService.instance) {
      OAuthService.instance = new OAuthService();
    }
    return OAuthService.instance;
  }

  // Microsoft OAuth Methods
  async loginWithMicrosoft(): Promise<{ user: OAuthUser; tokens: OAuthTokens }> {
    try {
      const loginRequest = {
        scopes: ['user.read', 'profile', 'email'],
        prompt: 'select_account',
      };

      const response: AuthenticationResult = await msalInstance.loginPopup(loginRequest);
      
      const user: OAuthUser = {
        id: response.account?.homeAccountId || '',
        email: response.account?.username || '',
        name: response.account?.name || '',
        picture: undefined, // Microsoft Graph API would be needed for profile picture
        provider: 'microsoft',
        organization: this.extractOrganization(response.account),
      };

      const tokens: OAuthTokens = {
        accessToken: response.accessToken,
        idToken: response.idToken,
        expiresAt: response.expiresOn?.getTime() || Date.now() + 3600000,
      };

      return { user, tokens };
    } catch (error) {
      console.error('Microsoft login error:', error);
      throw new Error('Failed to authenticate with Microsoft');
    }
  }

  async logoutMicrosoft(): Promise<void> {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length > 0) {
        await msalInstance.logoutPopup({
          account: accounts[0],
        });
      }
    } catch (error) {
      console.error('Microsoft logout error:', error);
    }
  }

  // Google OAuth Methods
  async loginWithGoogle(): Promise<{ user: OAuthUser; tokens: OAuthTokens }> {
    return new Promise((resolve, reject) => {
      if (typeof window === 'undefined' || !(window as any).google) {
        reject(new Error('Google OAuth not loaded'));
        return;
      }

      (window as any).google.accounts.oauth2.initCodeClient({
        client_id: GOOGLE_CLIENT_ID,
        scope: 'openid email profile',
        callback: async (response: any) => {
          try {
            // Exchange code for tokens
            const tokenResponse = await fetch('https://oauth2.googleapis.com/token', {
              method: 'POST',
              headers: {
                'Content-Type': 'application/x-www-form-urlencoded',
              },
              body: new URLSearchParams({
                client_id: GOOGLE_CLIENT_ID,
                code: response.code,
                grant_type: 'authorization_code',
                redirect_uri: window.location.origin,
              }),
            });

            const tokens = await tokenResponse.json();
            
            if (!tokens.access_token) {
              throw new Error('Failed to get access token');
            }

            // Get user info
            const userResponse = await fetch('https://www.googleapis.com/oauth2/v2/userinfo', {
              headers: {
                'Authorization': `Bearer ${tokens.access_token}`,
              },
            });

            const userInfo = await userResponse.json();

            const user: OAuthUser = {
              id: userInfo.id,
              email: userInfo.email,
              name: userInfo.name,
              picture: userInfo.picture,
              provider: 'google',
              organization: this.extractGoogleOrganization(userInfo.email),
            };

            const oauthTokens: OAuthTokens = {
              accessToken: tokens.access_token,
              refreshToken: tokens.refresh_token,
              idToken: tokens.id_token,
              expiresAt: Date.now() + (tokens.expires_in * 1000),
            };

            resolve({ user, tokens: oauthTokens });
          } catch (error) {
            console.error('Google authentication error:', error);
            reject(new Error('Failed to authenticate with Google'));
          }
        },
      }).requestCode();
    });
  }

  // Utility Methods
  private extractOrganization(account: AccountInfo | null): string | undefined {
    if (!account?.username) return undefined;
    
    const email = account.username;
    const domain = email.split('@')[1];
    
    // Common enterprise domains
    const enterpriseDomains = [
      'microsoft.com', 'google.com', 'amazon.com', 'apple.com',
      'facebook.com', 'netflix.com', 'uber.com', 'airbnb.com'
    ];
    
    if (enterpriseDomains.includes(domain)) {
      return domain.split('.')[0].toUpperCase();
    }
    
    return domain.split('.')[0].toUpperCase();
  }

  private extractGoogleOrganization(email: string): string | undefined {
    const domain = email.split('@')[1];
    return domain.split('.')[0].toUpperCase();
  }

  // Token Management
  async refreshMicrosoftToken(): Promise<OAuthTokens | null> {
    try {
      const accounts = msalInstance.getAllAccounts();
      if (accounts.length === 0) return null;

      const silentRequest = {
        scopes: ['user.read', 'profile', 'email'],
        account: accounts[0],
      };

      const response = await msalInstance.acquireTokenSilent(silentRequest);
      
      return {
        accessToken: response.accessToken,
        idToken: response.idToken,
        expiresAt: response.expiresOn?.getTime() || Date.now() + 3600000,
      };
    } catch (error) {
      console.error('Microsoft token refresh error:', error);
      return null;
    }
  }

  // Security Methods
  validateToken(token: string, provider: 'microsoft' | 'google'): boolean {
    try {
      // Basic JWT validation
      const parts = token.split('.');
      if (parts.length !== 3) return false;

      const payload = JSON.parse(atob(parts[1]));
      const now = Math.floor(Date.now() / 1000);
      
      return payload.exp > now;
    } catch (error) {
      return false;
    }
  }

  // Initialize OAuth providers
  async initializeProviders(): Promise<void> {
    try {
      // Initialize MSAL
      await msalInstance.initialize();
      
      // Load Google OAuth script
      if (typeof window !== 'undefined' && !(window as any).google) {
        await this.loadGoogleScript();
      }
    } catch (error) {
      console.error('OAuth initialization error:', error);
    }
  }

  private loadGoogleScript(): Promise<void> {
    return new Promise((resolve, reject) => {
      const script = document.createElement('script');
      script.src = 'https://accounts.google.com/gsi/client';
      script.async = true;
      script.defer = true;
      script.onload = () => resolve();
      script.onerror = () => reject(new Error('Failed to load Google OAuth script'));
      document.head.appendChild(script);
    });
  }
}

export default OAuthService.getInstance();
